<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  kanbanId: {
    type: [Number, String],
    required: true,
  },
  selectedStage: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const isEditing = computed(() => !!props.selectedStage?.id);
const uiFlags = computed(() => getters['kanbans/getUIFlags'].value);

const state = reactive({
  name: props.selectedStage?.name || '',
  color: props.selectedStage?.color || '#1f93ff',
});

watch(
  () => props.selectedStage,
  value => {
    state.name = value?.name || '';
    state.color = value?.color || '#1f93ff';
  }
);

const rules = { name: { required, minLength: minLength(1) }, color: {} };
const v$ = useVuelidate(rules, state);

const onClose = () => emit('close');

const submit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    if (isEditing.value) {
      await store.dispatch('kanbans/updateStage', {
        kanbanId: props.kanbanId,
        id: props.selectedStage.id,
        name: state.name,
        color: state.color,
      });
    } else {
      await store.dispatch('kanbans/createStage', {
        kanbanId: props.kanbanId,
        name: state.name,
        color: state.color,
      });
    }
    useAlert(t('KANBAN_SETTINGS.STAGE.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    useAlert(error.message || t('KANBAN_SETTINGS.STAGE.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        isEditing
          ? t('KANBAN_SETTINGS.STAGE.EDIT_TITLE')
          : t('KANBAN_SETTINGS.STAGE.ADD_TITLE')
      "
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="submit">
      <woot-input
        v-model="state.name"
        :class="{ error: v$.name.$error }"
        class="w-full"
        :label="t('KANBAN_SETTINGS.STAGE.FORM.NAME.LABEL')"
        :placeholder="t('KANBAN_SETTINGS.STAGE.FORM.NAME.PLACEHOLDER')"
        @input="v$.name.$touch"
        @blur="v$.name.$touch"
      />
      <div class="w-full">
        <label>
          {{ t('KANBAN_SETTINGS.STAGE.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="state.color" />
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="t('KANBAN_SETTINGS.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="
            isEditing
              ? t('KANBAN_SETTINGS.FORM.EDIT')
              : t('KANBAN_SETTINGS.FORM.CREATE')
          "
          :disabled="v$.name.$invalid"
          :is-loading="uiFlags.isCreatingStage || uiFlags.isUpdatingStage"
        />
      </div>
    </form>
  </div>
</template>
